import React, { useEffect, useRef, useState } from 'react';

type LandingContent = {
  markup: string;
  styles: string;
};

function publicAssetPath(assetName: string): string {
  const base = process.env.PUBLIC_URL || '';
  const normalizedBase = base.replace(/\/$/, '');
  const normalizedAsset = assetName.replace(/^\//, '');
  return normalizedBase ? `${normalizedBase}/${normalizedAsset}` : `/${normalizedAsset}`;
}

function parseLandingPage(source: string): LandingContent {
  const doc = new DOMParser().parseFromString(source, 'text/html');
  doc.querySelectorAll('script').forEach(script => script.remove());

  const navLinks = doc.getElementById('navlinks');
  const navCta = navLinks?.querySelector<HTMLAnchorElement>('.nav__cta');
  if (navLinks && navCta) {
    navCta.href = '#/portal';
    navCta.textContent = 'Portal';
    navCta.setAttribute('data-router-link', 'portal');

    if (!navLinks.querySelector('.nav__portal-spacer')) {
      const portalSpacer = doc.createElement('span');
      portalSpacer.className = 'nav__portal-spacer';
      portalSpacer.textContent = 'Portal';
      portalSpacer.setAttribute('aria-hidden', 'true');
      navLinks.insertBefore(portalSpacer, navCta);
    }
  }

  const styles = Array.from(doc.querySelectorAll('style'))
    .map(style => style.textContent || '')
    .join('\n');

  return {
    markup: doc.body.innerHTML,
    styles
  };
}

const PublicLandingPage: React.FC = () => {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [content, setContent] = useState<LandingContent | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    document.title = 'bonzaiQ - Intelligent product lifecycle';
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetch(publicAssetPath('bonzaiq-landing.html'))
      .then(response => {
        if (!response.ok) {
          throw new Error(`Unable to load landing page: ${response.status}`);
        }
        return response.text();
      })
      .then(html => {
        if (!cancelled) {
          setContent(parseLandingPage(html));
        }
      })
      .catch(() => {
        if (!cancelled) {
          setError('The bonzaiQ page could not be loaded.');
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!content || !rootRef.current) return;

    const root = rootRef.current;
    const cleanup: Array<() => void> = [];
    const burger = root.querySelector<HTMLElement>('#burger');
    const links = root.querySelector<HTMLElement>('#navlinks');
    const closeMenu = () => {
      links?.classList.remove('open');
      burger?.setAttribute('aria-expanded', 'false');
    };

    if (burger && links) {
      const onBurgerClick = () => {
        const open = links.classList.toggle('open');
        burger.setAttribute('aria-expanded', String(open));
      };
      burger.addEventListener('click', onBurgerClick);
      cleanup.push(() => burger.removeEventListener('click', onBurgerClick));
    }

    root.querySelectorAll<HTMLAnchorElement>('a[href^="#"]').forEach(anchor => {
      const href = anchor.getAttribute('href') || '';
      if (href.startsWith('#/')) {
        const onRouteLinkClick = () => closeMenu();
        anchor.addEventListener('click', onRouteLinkClick);
        cleanup.push(() => anchor.removeEventListener('click', onRouteLinkClick));
        return;
      }

      const targetId = href.slice(1);
      if (!targetId) return;

      const onSectionLinkClick = (event: MouseEvent) => {
        event.preventDefault();
        closeMenu();
        const target = root.querySelector<HTMLElement>(`#${targetId}`);
        target?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      };
      anchor.addEventListener('click', onSectionLinkClick);
      cleanup.push(() => anchor.removeEventListener('click', onSectionLinkClick));
    });

    const year = root.querySelector<HTMLElement>('#yr');
    if (year) {
      year.textContent = String(new Date().getFullYear());
    }

    const revealElements = Array.from(root.querySelectorAll<HTMLElement>('.reveal'));
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(
        entries => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              entry.target.classList.add('in');
              observer.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.16 }
      );
      revealElements.forEach(element => observer.observe(element));
      cleanup.push(() => observer.disconnect());
    } else {
      revealElements.forEach(element => element.classList.add('in'));
    }

    return () => {
      cleanup.forEach(run => run());
    };
  }, [content]);

  if (error) {
    return (
      <main style={fallbackStyles.wrap}>
        <p style={fallbackStyles.message}>{error}</p>
      </main>
    );
  }

  if (!content) {
    return (
      <main style={fallbackStyles.wrap}>
        <p style={fallbackStyles.message}>Loading bonzaiQ...</p>
      </main>
    );
  }

  return (
    <div ref={rootRef}>
      <style>{content.styles}</style>
      <div dangerouslySetInnerHTML={{ __html: content.markup }} />
    </div>
  );
};

const fallbackStyles: Record<string, React.CSSProperties> = {
  wrap: {
    minHeight: '100vh',
    display: 'grid',
    placeItems: 'center',
    background: '#fcf6f2',
    color: '#123734',
    padding: '1rem',
    fontFamily: 'Inter, system-ui, sans-serif'
  },
  message: {
    margin: 0,
    fontSize: '1rem',
    fontWeight: 600
  }
};

export default PublicLandingPage;
